// src/pages/auth/ForbiddenPage.jsx
import React, { useMemo, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShieldAlert, Home, ArrowLeft, LogIn } from "lucide-react";
import { useAuth } from "@/context/AuthProvider";

/* ───────────────── helpers ───────────────── */
function buildFromState(from) {
  try {
    if (!from) return "";
    const p = String(from.pathname || "");
    const s = String(from.search || "");
    const h = String(from.hash || "");
    return `${p}${s}${h}` || "";
  } catch {
    return "";
  }
}

function isInternalPath(p) {
  const s = String(p || "").trim();
  if (!s) return false;
  if (!s.startsWith("/")) return false;
  if (s.startsWith("//")) return false;
  return true;
}

function isBlockedLoopPath(p) {
  const s = String(p || "");
  return s.startsWith("/forbidden") || s.startsWith("/unauthorized") || s.startsWith("/login");
}

function isAdminPath(p) {
  const s = String(p || "");
  return s === "/admin" || s.startsWith("/admin/");
}

function isPlatformAdminUser(user) {
  if (!user) return false;
  if (user.is_platform_admin === true || user.isPlatformAdmin === true) return true;
  if (user.platform_admin === true || user.platformAdmin === true) return true;
  // IMPORTANT: do NOT treat developer as platform admin unless claim says so
  return false;
}

function safeReturnTarget(raw, { allowAdmin }) {
  const s = String(raw || "").trim();
  if (!isInternalPath(s)) return "";
  if (isBlockedLoopPath(s)) return "";
  if (!allowAdmin && isAdminPath(s)) return "";
  return s;
}

export default function ForbiddenPage() {
  const cardRef = useRef(null);
  const loc = useLocation();
  const auth = useAuth();

  const user = auth?.user || null;
  const allowAdmin = isPlatformAdminUser(user);

  const reason = loc.state?.reason || "forbidden";
  const refusedFrom = loc.state?.from || null;
  const refusedPath = refusedFrom?.pathname;

  const bestReturnTo = useMemo(() => {
    const fromState = safeReturnTarget(loc.state?.returnTo || "", { allowAdmin });
    const fromFrom = safeReturnTarget(buildFromState(refusedFrom), { allowAdmin });

    let fromQuery = "";
    try {
      const params = new URLSearchParams(loc.search || "");
      fromQuery = safeReturnTarget(params.get("returnTo") || "", { allowAdmin });
    } catch {
      fromQuery = "";
    }

    return fromState || fromFrom || fromQuery || "";
  }, [allowAdmin, refusedFrom, loc.search, loc.state?.returnTo]);

  // Deterministic fallback
  const fallback = "/dashboard";

  const returnHref = bestReturnTo || fallback;
  const returnLabel = bestReturnTo ? "Return to previous page" : "Back to Dashboard";

  const onMove = (e) => {
    const el = cardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    const rx = ((y - r.height / 2) / r.height) * 8;
    const ry = ((x - r.width / 2) / r.width) * -8;
    el.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg)`;
  };
  const onLeave = () => {
    const el = cardRef.current;
    if (el) el.style.transform = "perspective(900px) rotateX(0) rotateY(0)";
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#080b14] text-white">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-20 -left-24 h-96 w-96 rounded-full bg-fuchsia-500/20 blur-3xl" />
        <div className="absolute bottom-[-6rem] right-[-6rem] h-[28rem] w-[28rem] rounded-full bg-sky-500/20 blur-3xl" />
      </div>

      <style>{`
        @keyframes floatY { 0%{transform:translateY(0)} 50%{transform:translateY(-10px)} 100%{transform:translateY(0)} }
      `}</style>

      <div aria-hidden className="absolute inset-x-0 top-10 flex justify-center gap-6 select-none opacity-10">
        <span style={{ animation: "floatY 8s ease-in-out infinite" }} className="text-6xl font-black tracking-widest">
          SOLO
        </span>
        <span style={{ animation: "floatY 9s ease-in-out infinite", animationDelay: "0.3s" }} className="text-6xl font-black">
          .
        </span>
        <span style={{ animation: "floatY 8.5s ease-in-out infinite", animationDelay: "0.6s" }} className="text-6xl font-black tracking-widest">
          MDSH
        </span>
      </div>

      <div className="grid place-items-center min-h-screen p-6">
        <div
          ref={cardRef}
          onMouseMove={onMove}
          onMouseLeave={onLeave}
          className="group relative w-full max-w-2xl transition-transform duration-200"
        >
          <div className="absolute -inset-0.5 rounded-3xl bg-gradient-to-r from-rose-500/30 via-purple-500/30 to-sky-500/30 blur opacity-70 group-hover:opacity-100 transition" />
          <div className="relative rounded-3xl border border-white/10 bg-[#0f1222]/80 backdrop-blur-xl p-8 shadow-2xl">
            <div className="flex items-center gap-3 mb-2">
              <ShieldAlert className="w-7 h-7 text-rose-400" />
              <h1 className="text-3xl font-extrabold leading-tight">403 — Access denied</h1>
            </div>

            <p className="text-gray-300 mb-2">
              {reason === "platform_forbidden"
                ? "This area is restricted to platform administrators."
                : reason === "forbidden"
                ? "You don’t have permission to view this page with your current account."
                : "You don’t have permission to view this page."}
            </p>

            {refusedPath && (
              <p className="text-xs text-gray-400 mb-6">
                Refused path: <span className="font-mono">{refusedPath}</span>
              </p>
            )}

            <div className="flex flex-wrap gap-3">
              <Link to="/dashboard" className="inline-flex items-center gap-2 rounded-xl bg-white/10 hover:bg-white/20 px-4 py-2">
                <Home className="w-4 h-4" />
                <span>Dashboard</span>
              </Link>

              <Link
                to={returnHref}
                className="inline-flex items-center gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 px-4 py-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>{returnLabel}</span>
              </Link>

              <Link
                to={bestReturnTo ? `/login?returnTo=${encodeURIComponent(bestReturnTo)}` : "/login"}
                state={loc.state?.from ? { from: loc.state.from } : undefined}
                className="inline-flex items-center gap-2 rounded-xl border border-white/20 px-4 py-2 hover:bg-white/10"
              >
                <LogIn className="w-4 h-4" />
                <span>Switch account</span>
              </Link>
            </div>

            {bestReturnTo && (
              <p className="mt-4 text-[11px] text-gray-400">
                Return target: <span className="font-mono">{bestReturnTo}</span>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}